import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { auth, db } from '../../firebase';
import { toast } from 'react-hot-toast';
import BookingModal from '../../components/BookingModal';
import AdvancedBookingModal from '../../components/photographer/AdvancedBookingModal';

const ProfileView = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        let profileData;

        if (username) {
          const profilesRef = collection(db, 'profiles');
          const q = query(profilesRef, where('username', '==', username.toLowerCase()));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            profileData = {
              id: querySnapshot.docs[0].id,
              ...querySnapshot.docs[0].data()
            };
          } else {
            setError('Profile not found');
            return;
          }
        } else if (currentUser?.uid) {
          const profileRef = doc(db, 'profiles', currentUser.uid);
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            profileData = {
              id: profileSnap.id,
              ...profileSnap.data()
            };
          } else {
            sessionStorage.setItem('isNewUser', 'true');
            navigate('/app/onboarding');
            return;
          }
        } else {
          setError('Please sign in to view your profile');
          return;
        }

        setProfile(profileData);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Error loading profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username, navigate, currentUser]);

  const handleShareProfile = () => {
    const profileUrl = `${window.location.origin}/profile/${profile.username}`;
    navigator.clipboard.writeText(profileUrl);
    toast.success('Profile link copied!');
  };

  const handleBookNow = (service) => {
    setSelectedService({
      id: service.id || `service_${Date.now()}`,
      name: service.name,
      description: service.description,
      price: service.basePrice || service.price,
      currency: service.currency || 'USD',
    });

    const hasActiveServicePackages = profile.servicePackages?.some(pkg => pkg.isActive !== false && pkg.name && pkg.basePrice);

    if (hasActiveServicePackages || profile.portfolio?.categories) {
      setIsAdvancedModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-300 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full p-6">
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => navigate('/app')}
          className="px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  const isOwnProfile = currentUser?.uid && (!username || profile?.id === currentUser?.uid);

  // Get profile picture URL - prioritize currentUser.photoURL (Firebase auth) then profile fields
  const profilePic = currentUser?.photoURL || profile.profilePicture || profile.profilePhoto || profile.photoURL || profile.avatar;

  return (
    <div className="h-full overflow-auto bg-white dark:bg-black">
      <div className="max-w-2xl mx-auto px-4 py-8 pb-32">

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Avatar */}
          <div className="mb-4 relative inline-block">
            {profilePic ? (
              <img
                src={profilePic}
                alt={profile.name || 'Profile'}
                className="w-28 h-28 rounded-full object-cover mx-auto ring-4 ring-gray-100 dark:ring-[#333]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-28 h-28 rounded-full bg-gray-900 dark:bg-[#1A1A1A] items-center justify-center mx-auto ${profilePic ? 'hidden' : 'flex'}`}
              style={{ display: profilePic ? 'none' : 'flex' }}
            >
              <span className="text-3xl font-semibold text-white">
                {profile.name?.[0]?.toUpperCase() || profile.displayName?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          </div>

          {/* Name & Title */}
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
            {profile.name || profile.displayName || 'Your Name'}
          </h1>
          {profile.professionalTitle && (
            <p className="text-gray-500 dark:text-gray-400 mt-1">{profile.professionalTitle}</p>
          )}
          {profile.username && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">@{profile.username}</p>
          )}

          {/* Bio */}
          {profile.bio && (
            <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-md mx-auto leading-relaxed">
              {profile.bio}
            </p>
          )}

          {/* Action Buttons */}
          {isOwnProfile && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => navigate('/app/profile/edit')}
                className="px-5 py-2.5 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                Edit Profile
              </button>
              <button
                onClick={handleShareProfile}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
              >
                Share
              </button>
            </div>
          )}
        </motion.div>

        {/* Service Packages */}
        {(() => {
          const activePackages = profile.servicePackages?.filter(pkg =>
            pkg.isActive !== false && pkg.name && pkg.basePrice
          ) || [];

          if (activePackages.length === 0) return null;

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <h2 className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Services
              </h2>
              <div className="space-y-4">
                {activePackages.map((pkg, index) => (
                  <div
                    key={pkg.id || index}
                    className="p-5 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl hover:border-gray-300 dark:hover:border-[#333] hover:shadow-sm transition-all"
                  >
                    {pkg.image && (
                      <img
                        src={pkg.image}
                        alt={pkg.name}
                        className="w-full h-40 object-cover rounded-xl mb-4"
                      />
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pkg.name}</h3>
                        {pkg.popular && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium text-white bg-gray-900 dark:bg-[#FFB27D] dark:text-black rounded-full mt-1">
                            Popular
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          ${pkg.basePrice?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">starting from</p>
                      </div>
                    </div>

                    {pkg.description && (
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">{pkg.description}</p>
                    )}

                    {pkg.duration && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{pkg.duration}</span>
                      </div>
                    )}

                    {pkg.includedServices?.length > 0 && (
                      <ul className="space-y-1.5 mb-4">
                        {pkg.includedServices.slice(0, 4).map((service, idx) => (
                          <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {service}
                          </li>
                        ))}
                        {pkg.includedServices.length > 4 && (
                          <li className="text-sm text-gray-400 dark:text-gray-500">
                            +{pkg.includedServices.length - 4} more
                          </li>
                        )}
                      </ul>
                    )}

                    {!isOwnProfile && (
                      <button
                        onClick={() => handleBookNow(pkg)}
                        className="w-full py-2.5 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                      >
                        Book Now
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })()}

        {/* Regular Services (if no packages) */}
        {(() => {
          const hasPackages = profile.servicePackages?.some(pkg =>
            pkg.isActive !== false && pkg.name && pkg.basePrice
          );

          if (hasPackages || !profile.services?.length) return null;

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <h2 className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Services
              </h2>
              <div className="space-y-3">
                {profile.services.map((service, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-xl hover:border-gray-300 dark:hover:border-[#333] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">{service.name}</h3>
                        {service.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{service.description}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">${service.price}</p>
                      </div>
                    </div>
                    {!isOwnProfile && (
                      <button
                        onClick={() => handleBookNow(service)}
                        className="w-full mt-3 py-2 text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-[#1A1A1A] rounded-lg hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
                      >
                        Book Now
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })()}

        {/* Portfolio */}
        {(() => {
          let items = [];
          if (Array.isArray(profile.portfolio)) {
            items = profile.portfolio;
          } else if (profile.portfolio?.items) {
            items = profile.portfolio.items;
          } else if (profile.portfolio?.featured) {
            items = profile.portfolio.featured;
          }

          if (items.length === 0) return null;

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                Portfolio
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {items.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-[#1A1A1A] group cursor-pointer"
                  >
                    {(item.image || item.imageUrl) && (
                      <img
                        src={item.image || item.imageUrl}
                        alt={item.title || ''}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })()}

        {/* Contact Info */}
        {(profile.contactInfo?.email || profile.contactInfo?.phone || profile.contactInfo?.location) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
              Contact
            </h2>
            <div className="space-y-3">
              {profile.contactInfo?.email && (
                <a
                  href={`mailto:${profile.contactInfo.email}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#111111] rounded-xl hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-[#2A2A2A] flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Email</p>
                    <p className="text-sm text-gray-900 dark:text-white">{profile.contactInfo.email}</p>
                  </div>
                </a>
              )}
              {profile.contactInfo?.phone && (
                <a
                  href={`tel:${profile.contactInfo.phone}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#111111] rounded-xl hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-[#2A2A2A] flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900 dark:text-white">{profile.contactInfo.phone}</p>
                  </div>
                </a>
              )}
              {profile.contactInfo?.location && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#111111] rounded-xl">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-[#2A2A2A] flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Location</p>
                    <p className="text-sm text-gray-900 dark:text-white">{profile.contactInfo.location}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Social Links */}
        {profile.socialLinks && Object.keys(profile.socialLinks).some(k => profile.socialLinks[k]) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
              Links
            </h2>
            <div className="flex flex-wrap gap-2">
              {profile.socialLinks.instagram && (
                <a
                  href={`https://instagram.com/${profile.socialLinks.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </a>
              )}
              {profile.socialLinks.twitter && (
                <a
                  href={`https://twitter.com/${profile.socialLinks.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Twitter
                </a>
              )}
              {profile.socialLinks.linkedin && (
                <a
                  href={`https://linkedin.com/in/${profile.socialLinks.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </a>
              )}
              {profile.socialLinks.website && (
                <a
                  href={profile.socialLinks.website.startsWith('http') ? profile.socialLinks.website : `https://${profile.socialLinks.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2A2A2A] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Website
                </a>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Booking Modals */}
      {isModalOpen && selectedService && (
        <BookingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          service={selectedService}
          profileId={profile.id}
        />
      )}

      {isAdvancedModalOpen && (
        <AdvancedBookingModal
          isOpen={isAdvancedModalOpen}
          onClose={() => setIsAdvancedModalOpen(false)}
          photographer={profile}
          initialService={selectedService}
          onFallbackToBasic={() => {
            setIsAdvancedModalOpen(false);
            setIsModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default ProfileView;
